/**
 * AI receptionists — the tenant's conversational bots (ai-operations agent
 * roles), reached through the operations proxy at `/api/messaging/ai-agents`
 * (operations is the FE's auth boundary + RBAC gate; it forwards to
 * ai-operations with the shared service key). Powers:
 *   - Settings → AI Chatbot   (create / configure receptionists)
 *   - Chatbot Simulator       (test a receptionist)
 *   - Settings → Channels      (pick which bot answers a channel)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import type { FlowGraph } from '@/lib/ai/flow-types';

/** Conversation engine mode. PROMPT = free-form system prompt; FLOW = a
 * structured node/edge graph driven by the drag-and-drop flow builder. */
export type BotMode = 'PROMPT' | 'FLOW';

const BASE = '/api/messaging/ai-agents';

/** An intent pattern on the entry-point bot that hands a conversation off to a
 * specialist bot (`targetRoleId`; null = the entry-point handles it itself). */
export interface RoutingRule {
  id: string;
  intentPattern: string;
  /** The specialist bot this intent routes to; null = handled by the owner. */
  targetRoleId: string | null;
  priority: number;
  description: string | null;
}

export interface AiReceptionist {
  id: string;
  tenantId: string;
  name: string;
  displayName: string;
  description: string | null;
  systemPrompt: string | null;
  /** Optional first-contact greeting (typically only the receptionist has one). */
  welcomeMessage: string | null;
  /** Platform template this bot was instantiated from, if any. */
  builtinAgentId: string | null;
  /** Routes to specialists instead of answering directly. */
  isRouter: boolean;
  /** The entry-point bot that greets every new customer and routes to
   * specialists. Exactly one bot per tenant is the entry point. */
  isEntrypoint: boolean;
  isCustom: boolean;
  enabled: boolean;
  sortOrder: number;
  /** Conversation engine mode — PROMPT (system prompt) or FLOW (graph). */
  mode: BotMode;
  /** Structured flow graph when `mode === 'FLOW'`; null otherwise. */
  flowGraph: FlowGraph | null;
  /** Intent patterns (on the entry-point bot) that hand off to a specialist. */
  routingRules: RoutingRule[];
}

export interface CreateReceptionistPayload {
  name: string;
  displayName: string;
  description?: string;
  systemPrompt?: string;
  welcomeMessage?: string;
  builtinAgentId?: string;
  isRouter?: boolean;
  isEntrypoint?: boolean;
  enabled?: boolean;
}

export interface UpdateReceptionistPayload {
  displayName?: string;
  description?: string;
  systemPrompt?: string;
  welcomeMessage?: string;
  isRouter?: boolean;
  isEntrypoint?: boolean;
  enabled?: boolean;
  sortOrder?: number;
  mode?: BotMode;
  flowGraph?: FlowGraph | null;
}

export interface ReceptionistChatReply {
  replyText: string | null;
  handledByRole: string | null;
  conversationId: string;
  escalated: boolean;
  handoffReason: { triggerType: string; triggerDetail: string | null } | null;
}

export interface ReceptionistChatRequest {
  message: string;
  conversationId?: string;
  /** Simulated customer's source phone — a fresh one = a new customer. */
  customerPhone?: string;
  channel?: 'sms' | 'voice' | 'chat';
}

export const aiReceptionistsKey = ['messaging', 'ai-receptionists'] as const;

export function useAiReceptionists() {
  return useQuery({
    queryKey: aiReceptionistsKey,
    queryFn: () => apiGet<AiReceptionist[]>(operationsClient, BASE),
    staleTime: 30_000,
  });
}

export function useCreateReceptionist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReceptionistPayload) =>
      apiPost<AiReceptionist, CreateReceptionistPayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiReceptionistsKey });
    },
  });
}

export function useUpdateReceptionist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateReceptionistPayload) =>
      apiPut<AiReceptionist, UpdateReceptionistPayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiReceptionistsKey });
    },
  });
}

/** Save a bot's mode + flow graph (the drag-and-drop flow builder). Sends
 * `{ mode, flowGraph }` through the same update proxy → ai-ops PUT /roles/:id. */
export function useUpdateBotFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      mode,
      flowGraph,
    }: {
      id: string;
      mode: BotMode;
      flowGraph: FlowGraph | null;
    }) =>
      apiPut<AiReceptionist, UpdateReceptionistPayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        { mode, flowGraph },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiReceptionistsKey });
    },
  });
}

export function useDeleteReceptionist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiReceptionistsKey });
    },
  });
}

export function useAddRoutingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleId,
      intentPattern,
      targetRoleId,
    }: {
      roleId: string;
      intentPattern: string;
      /** Specialist bot the intent hands off to; omit = handled by the owner. */
      targetRoleId?: string;
    }) =>
      apiPost<RoutingRule, { intentPattern: string; targetRoleId?: string }>(
        operationsClient,
        `${BASE}/${encodeURIComponent(roleId)}/routing-rules`,
        { intentPattern, ...(targetRoleId ? { targetRoleId } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiReceptionistsKey });
    },
  });
}

export function useDeleteRoutingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, ruleId }: { roleId: string; ruleId: string }) =>
      apiDelete<void>(
        operationsClient,
        `${BASE}/${encodeURIComponent(roleId)}/routing-rules/${encodeURIComponent(ruleId)}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiReceptionistsKey });
    },
  });
}

/** Send a message to the receptionist engine and get its reply (simulator). */
export function useTestReceptionist() {
  return useMutation({
    mutationFn: (body: ReceptionistChatRequest) =>
      apiPost<ReceptionistChatReply, ReceptionistChatRequest>(
        operationsClient,
        `${BASE}/test`,
        body,
      ),
  });
}

export function useResumeTestReceptionist() {
  return useMutation({
    mutationFn: (conversationId: string) =>
      apiPost<{ conversationId: string; status: 'ACTIVE'; mode: 'ai' }>(
        operationsClient,
        `${BASE}/test/${encodeURIComponent(conversationId)}/resume`,
        {},
      ),
  });
}

// ── Iris insights (Analyze dashboard) ───────────────────────────────────────

/** Fin-style Analyze metrics for the tenant's AI service agent (Iris). Mirrors
 * ai-operations' `getIrisInsights`, reached through the operations proxy. */
export interface IrisInsights {
  windowDays: number;
  analyzedTotal: number;
  pendingAnalysis: number;
  /** Resolved ÷ (analyzed − constrained); null when nothing analyzed. */
  resolutionRate: number | null;
  /** Confirmed resolutions ÷ (analyzed − constrained) — the honest subset. */
  confirmedResolutionRate: number | null;
  /** Resolution split: confirmed (customer acknowledged) vs assumed (went quiet). */
  resolution: { total: number; confirmed: number; assumed: number };
  escalationRate: number | null;
  outcomes: Record<string, number>;
  sentiment: Record<string, number>;
  cxScore: {
    average: number | null;
    rated: number;
    distribution: Record<string, number>;
  };
  topTopics: Array<{ topic: string | null; count: number }>;
}

export const irisInsightsKey = (days: number) => ['messaging', 'iris-insights', days] as const;

export function useIrisInsights(days = 30) {
  return useQuery({
    queryKey: irisInsightsKey(days),
    queryFn: () => apiGet<IrisInsights>(operationsClient, `${BASE}/insights?days=${days}`),
    staleTime: 30_000,
  });
}

/** Backfill: analyze recent terminal conversations that lack Iris attributes. */
export function useAnalyzePendingConversations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit?: number) =>
      apiPost<{ analyzed: number }, { limit?: number }>(
        operationsClient,
        `${BASE}/analyze-pending`,
        limit ? { limit } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['messaging', 'iris-insights'] });
    },
  });
}

// ── Iris conversations (Fin-style outcome views) ─────────────────────────────

export type IrisOutcome =
  | 'PENDING'
  | 'RESOLVED'
  | 'ESCALATED_HANDOFF'
  | 'ABANDONED'
  | 'SPAM'
  | 'CONSTRAINED';

export interface IrisConversationRow {
  id: string;
  customerPhone: string;
  status: string;
  messageCount: number;
  leadName: string | null;
  leadStatus: string | null;
  createdAt: string;
  lastActivity: string;
  outcome: IrisOutcome;
  aiTitle: string | null;
  topic: string | null;
  sentiment: string | null;
  urgency: string | null;
  cxScore: number | null;
}

export interface IrisConversationList {
  items: IrisConversationRow[];
  nextCursor: string | null;
}

export interface IrisConversationDetail {
  id: string;
  customerPhone: string;
  status: string;
  createdAt: string;
  mode: string;
  lead: { name?: string | null; email?: string | null; address?: string | null } | null;
  iris: {
    outcome: IrisOutcome;
    aiTitle: string | null;
    topic: string | null;
    sentiment: string | null;
    complexity: string | null;
    urgency: string | null;
    cxScore: number | null;
    cxExplanation: string | null;
    analyzedAt: string | null;
    resolvedAt: string | null;
  };
  messages: Array<{
    id: string;
    direction: 'INBOUND' | 'OUTBOUND' | 'SYSTEM';
    body: string;
    handledByRole: string | null;
    channel: string | null;
    sentAt: string;
  }>;
}

/** List Iris conversations, optionally filtered by outcome. `outcome` omitted =
 * all. */
export function useIrisConversations(outcome?: IrisOutcome) {
  return useQuery({
    queryKey: ['messaging', 'iris-conversations', outcome ?? 'ALL'] as const,
    queryFn: () => {
      const qs = outcome ? `?outcome=${outcome}` : '';
      return apiGet<IrisConversationList>(operationsClient, `${BASE}/conversations${qs}`);
    },
    staleTime: 15_000,
  });
}

/** A single Iris conversation — transcript + AI attributes. */
export function useIrisConversation(id: string | null) {
  return useQuery({
    queryKey: ['messaging', 'iris-conversation', id] as const,
    enabled: !!id,
    queryFn: () =>
      apiGet<IrisConversationDetail>(
        operationsClient,
        `${BASE}/conversations/${encodeURIComponent(id as string)}`,
      ),
  });
}
